import { useState } from "react";
import { motion } from "framer-motion";
import {
  useBriefing,
  useOpportunities,
  useRevenueSummary,
} from "@/hooks/use-mock-data";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DoomsdayGauge } from "@/components/strangelove/DoomsdayGauge";
import { BentoCard } from "@/components/strangelove/BentoCard";
import { CompactOpportunityCard } from "@/components/strangelove/CompactOpportunityCard";
import { RevenueStreamsSummary } from "@/components/strangelove/RevenueStreamsSummary";
import { MilestonesSummary } from "@/components/strangelove/MilestonesSummary";
import { VenturesSummary } from "@/components/strangelove/VenturesSummary";
import { SparklineChart } from "@/components/strangelove/SparklineChart";
import { useCountUp } from "@/hooks/use-count-up";
import {
  Radar,
  Zap,
  DollarSign,
  Flag,
  Rocket,
  TrendingUp,
  Crosshair,
  Gauge,
  ArrowRight,
  CheckCircle,
  FlaskConical,
} from "lucide-react";

export default function BigBoardPage() {
  const { data: briefing, isLoading: briefingLoading } = useBriefing();
  const { data: opportunities, isLoading: oppsLoading, refetch: refetchOpps } = useOpportunities();
  const { data: revSummary } = useRevenueSummary();
  const [scanning, setScanning] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);

  const handleSynthesis = async () => {
    setSynthesizing(true);
    setScanResult("Running synthesis on existing signals...");
    try {
      const synthRes = await api.post<{
        message: string;
        clustering: { clustersCreated: number; qualified: number };
        synthesis: { opportunitiesCreated: number; opportunitiesUpdated: number };
      }>("/api/synthesis/run");

      const { clustering, synthesis } = synthRes;
      setScanResult(
        `${clustering.clustersCreated} clusters → ${synthesis.opportunitiesCreated} new opportunities`
      );
      toast.success(synthRes.message);
      refetchOpps();
    } catch (err) {
      setScanResult("Synthesis failed");
      toast.error(err instanceof Error ? err.message : "Synthesis failed.");
    } finally {
      setSynthesizing(false);
    }
  };

  const gaugeValue = useCountUp(Math.round(revSummary.percentage * 10) / 10, 800);
  const top6 = [...opportunities]
    .sort((a, b) => b.compositeScore - a.compositeScore)
    .slice(0, 6);
  const totalOpps = opportunities.length;

  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      // Step 1: Scan (~15s)
      const scanRes = await api.post<{
        message: string;
        scanLogId: string;
        status: string;
        signalsStored?: number;
        signalsFetched?: number;
        signalsFiltered?: number;
      }>("/api/scans/trigger");

      const stored = scanRes.signalsStored ?? 0;
      setScanning(false);

      if (stored === 0) {
        setScanResult(`${scanRes.signalsFetched ?? 0} signals scanned — no new signals`);
        toast.success(scanRes.message);
        return;
      }

      setScanResult(`${stored} signals acquired — synthesizing...`);
      toast.success(`${stored} signals acquired. Running synthesis...`);

      // Step 2: Auto-synthesis (~15s)
      setSynthesizing(true);
      try {
        const synthRes = await api.post<{
          message: string;
          clustering: { clustersCreated: number; qualified: number };
          synthesis: { opportunitiesCreated: number; opportunitiesUpdated: number };
        }>("/api/synthesis/run");

        const { clustering, synthesis } = synthRes;
        setScanResult(
          `${stored} signals → ${clustering.clustersCreated} clusters → ${synthesis.opportunitiesCreated} opportunities`
        );
        toast.success(synthRes.message);
        refetchOpps();
      } catch (err) {
        setScanResult(`${stored} signals acquired — synthesis failed`);
        toast.error("Synthesis failed. Run manually from settings.");
      } finally {
        setSynthesizing(false);
      }
    } catch (err) {
      setScanning(false);
      setScanResult("Scan error");
      toast.error(err instanceof Error ? err.message : "Failed to trigger scan.");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Bento grid */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 auto-rows-auto"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.06 } },
        }}
      >
        {/* Morning Briefing (3 cols) */}
        <BentoCard
          title="Morning Briefing"
          icon={Zap}
          span="wide"
          className="md:col-span-3"
          index={0}
        >
          <div className="relative scanlines overflow-hidden rounded-lg">
            {(scanning || synthesizing) && (
              <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                <div
                  className="w-[300px] h-[300px] animate-radar-sweep"
                  style={{
                    background:
                      "conic-gradient(from 0deg, transparent 0deg, rgba(68, 242, 210, 0.06) 0deg, rgba(68, 242, 210, 0.01) 60deg, transparent 60deg)",
                  }}
                />
              </div>
            )}

            {briefingLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : (
              <>
                <p className="font-brand text-2xl md:text-3xl text-foreground mb-0.5">
                  {briefing.greeting.split(".")[0]}.
                </p>
                <p className="text-[#5A6A68] text-[12px] mb-4 italic">
                  "{briefing.strangeloveQuote}"
                </p>

                <ul className="space-y-1.5 mb-4">
                  {briefing.topActions.map((action, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[13px] text-foreground"
                    >
                      <CheckCircle className="w-3.5 h-3.5 text-electric-blue mt-0.5 shrink-0" />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {briefing.overnightScan.newOpportunities > 0 && (
              <div className="inline-flex items-center gap-2 text-[11px] text-[#8A9A98] bg-[rgba(255,255,255,0.04)] rounded-lg px-2.5 py-1.5">
                <Radar className="w-3.5 h-3.5 text-mint" />
                <span>
                  <span className="font-mono text-mint">
                    {briefing.overnightScan.newOpportunities}
                  </span>{" "}
                  new detected &middot; Top score:{" "}
                  <span className="font-mono text-foreground">
                    {briefing.overnightScan.topScore}
                  </span>
                </span>
              </div>
            )}
          </div>
        </BentoCard>

        {/* Doomsday Gauge (1 col) */}
        <BentoCard
          title="Doomsday Gauge"
          description={`${gaugeValue}% to target`}
          icon={Gauge}
          href="/revenue"
          index={1}
        >
          <div className="flex flex-col items-center gap-3">
            <DoomsdayGauge
              current={revSummary.currentMonth}
              target={revSummary.target}
              size="sm"
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                handleScan();
              }}
              disabled={scanning || synthesizing}
              className="relative bg-electric-blue hover:bg-electric-blue/90 text-white font-semibold text-[11px] uppercase tracking-wider px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {!scanning && !synthesizing && !scanResult && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-deploy-ping absolute inline-flex h-full w-full rounded-full bg-electric-blue opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-electric-blue" />
                </span>
              )}
              <Radar className="w-3.5 h-3.5 inline mr-1.5" />
              {scanning ? "SCANNING..." : synthesizing ? "SYNTHESIZING..." : "DEPLOY"}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                handleSynthesis();
              }}
              disabled={scanning || synthesizing}
              className="bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.1)] text-mint font-semibold text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <FlaskConical className="w-3 h-3 inline mr-1" />
              SYNTHESIZE
            </button>
            {scanResult && (
              <p className="text-[10px] text-mint text-center mt-1">{scanResult}</p>
            )}
          </div>
        </BentoCard>

        {/* Top Opportunities (3 cols, 2 rows) */}
        <BentoCard
          title="Opportunities"
          description={`${totalOpps} total`}
          icon={Crosshair}
          span="wide"
          className="md:col-span-3 md:row-span-2"
          href="/opportunities"
          index={2}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {oppsLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-[72px] rounded-lg" />
                ))
              : top6.map((opp) => (
                  <CompactOpportunityCard key={opp.id} opportunity={opp} />
                ))}
          </div>
          <span className="inline-flex items-center gap-1 mt-3 text-[11px] text-electric-blue">
            View all {totalOpps} <ArrowRight className="w-3 h-3" />
          </span>
        </BentoCard>

        {/* Revenue Streams (1 col, tall) */}
        <BentoCard
          title="Revenue Streams"
          icon={DollarSign}
          span="tall"
          href="/revenue"
          index={3}
        >
          <RevenueStreamsSummary />
        </BentoCard>

        {/* Milestones (2 cols) */}
        <BentoCard
          title="Milestones"
          description="Top 3 active"
          icon={Flag}
          span="wide"
          href="/milestones"
          index={4}
        >
          <MilestonesSummary />
        </BentoCard>

        {/* Ventures (1 col) */}
        <BentoCard
          title="Ventures"
          icon={Rocket}
          href="/ventures"
          index={5}
        >
          <VenturesSummary />
        </BentoCard>

        {/* Sparkline (1 col) */}
        <BentoCard
          title="Monthly Trend"
          icon={TrendingUp}
          index={6}
        >
          <SparklineChart />
        </BentoCard>
      </motion.div>
    </div>
  );
}
